'use client';

import { useState } from 'react';
import { useLocale } from 'next-intl';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Link } from '@/i18n/routing';

type ContactFormCopy = {
  name: string;
  namePlaceholder: string;
  phone: string;
  phonePlaceholder: string;
  subject: string;
  subjectPlaceholder: string;
  message: string;
  messagePlaceholder: string;
  privacyStart: string;
  privacy: string;
  privacyEnd: string;
  send: string;
  sending: string;
  success: string;
  error: string;
};

export function ContactForm({ copy }: { copy: ContactFormCopy }) {
  const locale = useLocale();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // `currentTarget` is only guaranteed during the synchronous event dispatch.
    // Keep the form reference before awaiting the request, otherwise a successful
    // submission can still end in the error state when reset() is called.
    const form = event.currentTarget;
    setStatus('sending');
    setErrorMessage('');

    const formData = new FormData(event.currentTarget);
    const payload = {
      name: String(formData.get('name') || ''),
      email: String(formData.get('email') || ''),
      phone: String(formData.get('phone') || ''),
      subject: String(formData.get('subject') || ''),
      message: String(formData.get('message') || ''),
      locale,
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || copy.error);
      }

      const [firstName, ...lastNameParts] = payload.name.trim().split(/\s+/);
      window.printenTrack?.('lead', {
        eventId: String(data.eventId || `contact_${Date.now()}`),
        email: payload.email,
        phone: payload.phone,
        firstName,
        lastName: lastNameParts.join(' '),
        source: 'contact_form',
        value: 0,
        currency: 'EUR',
      });

      form.reset();
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : copy.error);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.name} *
          </label>
          <Input id="name" name="name" type="text" required placeholder={copy.namePlaceholder} className="w-full" />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email *
          </label>
          <Input id="email" name="email" type="email" required placeholder="jouw@email.nl" className="w-full" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.phone}
          </label>
          <Input id="phone" name="phone" type="tel" placeholder={copy.phonePlaceholder} className="w-full" />
        </div>
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.subject} *
          </label>
          <Input id="subject" name="subject" type="text" required placeholder={copy.subjectPlaceholder} className="w-full" />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          {copy.message} *
        </label>
        <Textarea id="message" name="message" required rows={6} placeholder={copy.messagePlaceholder} className="w-full" />
      </div>

      <div className="flex items-start gap-2">
        <input type="checkbox" id="privacy" required className="mt-1" />
        <label htmlFor="privacy" className="text-sm text-gray-600">
          {copy.privacyStart}{' '}
          <Link href="/privacy" className="text-blue-600 hover:text-blue-700">
            {copy.privacy}
          </Link>{' '}
          {copy.privacyEnd}
        </label>
      </div>

      {status === 'success' && (
        <p className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{copy.success}</p>
      )}
      {status === 'error' && (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{errorMessage || copy.error}</p>
      )}

      <Button type="submit" size="lg" className="w-full md:w-auto" disabled={status === 'sending'}>
        <Mail className="h-4 w-4 mr-2" />
        {status === 'sending' ? copy.sending : copy.send}
      </Button>
    </form>
  );
}
